import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag, Package, Truck, CreditCard, Lock, CheckCircle, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// US States with tax rates (as of 2024 - combined state + average local rates)
const US_STATES = [
  { code: 'AL', name: 'Alabama', taxRate: 0.0922 },
  { code: 'AK', name: 'Alaska', taxRate: 0.0176 },
  { code: 'AZ', name: 'Arizona', taxRate: 0.0842 },
  { code: 'AR', name: 'Arkansas', taxRate: 0.0947 },
  { code: 'CA', name: 'California', taxRate: 0.0868 },
  { code: 'CO', name: 'Colorado', taxRate: 0.0777 },
  { code: 'CT', name: 'Connecticut', taxRate: 0.0635 },
  { code: 'DE', name: 'Delaware', taxRate: 0.0000 },
  { code: 'FL', name: 'Florida', taxRate: 0.0701 },
  { code: 'GA', name: 'Georgia', taxRate: 0.0732 },
  { code: 'HI', name: 'Hawaii', taxRate: 0.0444 },
  { code: 'ID', name: 'Idaho', taxRate: 0.0602 },
  { code: 'IL', name: 'Illinois', taxRate: 0.0882 },
  { code: 'IN', name: 'Indiana', taxRate: 0.0700 },
  { code: 'IA', name: 'Iowa', taxRate: 0.0694 },
  { code: 'KS', name: 'Kansas', taxRate: 0.0869 },
  { code: 'KY', name: 'Kentucky', taxRate: 0.0600 },
  { code: 'LA', name: 'Louisiana', taxRate: 0.0955 },
  { code: 'ME', name: 'Maine', taxRate: 0.0550 },
  { code: 'MD', name: 'Maryland', taxRate: 0.0600 },
  { code: 'MA', name: 'Massachusetts', taxRate: 0.0625 },
  { code: 'MI', name: 'Michigan', taxRate: 0.0600 },
  { code: 'MN', name: 'Minnesota', taxRate: 0.0746 },
  { code: 'MS', name: 'Mississippi', taxRate: 0.0707 },
  { code: 'MO', name: 'Missouri', taxRate: 0.0825 },
  { code: 'MT', name: 'Montana', taxRate: 0.0000 },
  { code: 'NE', name: 'Nebraska', taxRate: 0.0694 },
  { code: 'NV', name: 'Nevada', taxRate: 0.0823 },
  { code: 'NH', name: 'New Hampshire', taxRate: 0.0000 },
  { code: 'NJ', name: 'New Jersey', taxRate: 0.0660 },
  { code: 'NM', name: 'New Mexico', taxRate: 0.0783 },
  { code: 'NY', name: 'New York', taxRate: 0.0852 },
  { code: 'NC', name: 'North Carolina', taxRate: 0.0698 },
  { code: 'ND', name: 'North Dakota', taxRate: 0.0696 },
  { code: 'OH', name: 'Ohio', taxRate: 0.0723 },
  { code: 'OK', name: 'Oklahoma', taxRate: 0.0895 },
  { code: 'OR', name: 'Oregon', taxRate: 0.0000 },
  { code: 'PA', name: 'Pennsylvania', taxRate: 0.0634 },
  { code: 'RI', name: 'Rhode Island', taxRate: 0.0700 },
  { code: 'SC', name: 'South Carolina', taxRate: 0.0746 },
  { code: 'SD', name: 'South Dakota', taxRate: 0.0640 },
  { code: 'TN', name: 'Tennessee', taxRate: 0.0955 },
  { code: 'TX', name: 'Texas', taxRate: 0.0820 },
  { code: 'UT', name: 'Utah', taxRate: 0.0719 },
  { code: 'VT', name: 'Vermont', taxRate: 0.0624 },
  { code: 'VA', name: 'Virginia', taxRate: 0.0575 },
  { code: 'WA', name: 'Washington', taxRate: 0.0929 },
  { code: 'WV', name: 'West Virginia', taxRate: 0.0652 },
  { code: 'WI', name: 'Wisconsin', taxRate: 0.0543 },
  { code: 'WY', name: 'Wyoming', taxRate: 0.0536 },
  { code: 'DC', name: 'Washington D.C.', taxRate: 0.0600 },
  { code: 'PR', name: 'Puerto Rico', taxRate: 0.1150 },
  { code: 'VI', name: 'U.S. Virgin Islands', taxRate: 0.0000 },
];

// Shipping rates based on order subtotal
const SHIPPING_RATES = [
  { maxSubtotal: 25, rate: 3.99, label: 'Standard Shipping (5-7 days)' },
  { maxSubtotal: 50, rate: 5.99, label: 'Standard Shipping (5-7 days)' },
  { maxSubtotal: 74, rate: 7.99, label: 'Standard Shipping (5-7 days)' },
  { maxSubtotal: Infinity, rate: 0, label: 'FREE Shipping (5-7 days)' },
];

const FREE_SHIPPING_THRESHOLD = 75; // Free shipping for orders over $75

// Countries list for billing address
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'JP', name: 'Japan' },
  { code: 'BR', name: 'Brazil' },
];

const getShippingRate = (subtotal) => {
  if (subtotal >= FREE_SHIPPING_THRESHOLD) {
    return { rate: 0, label: 'FREE Shipping (5-7 days)' };
  }
  for (const tier of SHIPPING_RATES) {
    if (subtotal <= tier.maxSubtotal) {
      return { rate: tier.rate, label: tier.label };
    }
  }
  return { rate: 7.99, label: 'Standard Shipping (5-7 days)' };
};

const getTaxRate = (stateCode) => {
  const state = US_STATES.find(s => s.code === stateCode);
  return state ? state.taxRate : 0;
};

// Checkout Success Page Component
export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      if (orderId) {
        try {
          const response = await axios.get(`${API}/orders`);
          const foundOrder = response.data.find(o => o.id === orderId);
          if (foundOrder) {
            setOrder(foundOrder);
          }
        } catch (error) {
          console.error('Error fetching order:', error);
        }
      }
      setLoading(false);
    };
    fetchOrder();
  }, [orderId]);

  return (
    <div data-testid="checkout-success" className="min-h-screen pt-28 pb-16">
      <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-4">
          Thank You for Your Order!
        </h1>
        <p className="text-gray-600 mb-8">
          Your payment was successful. We've sent a confirmation email with your order details.
        </p>
        
        {order && (
          <div className="bg-white p-6 rounded-2xl text-left mb-8">
            <h2 className="font-semibold text-forest mb-4">Order Details</h2>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-500">Order ID:</span> {order.id.slice(0, 8)}...</p>
              <p><span className="text-gray-500">Email:</span> {order.customer_email}</p>
              <p><span className="text-gray-500">Total:</span> ${order.total_amount?.toFixed(2)}</p>
            </div>
            <div className="mt-4 p-3 bg-sand/50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Shipping:</strong> Your order will be shipped within 48 hours. 
                Expected delivery is 5-7 business days after shipping.
              </p>
            </div>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/shop">
            <Button className="bg-forest text-white rounded-full px-8">
              Continue Shopping
            </Button>
          </Link>
          <Link to="/track-order">
            <Button variant="outline" className="rounded-full px-8">
              Track Your Order
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export const CartPage = () => {
  const { items, updateQuantity, removeItem, totalAmount, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Cart, 2: Checkout, 3: Payment
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [acceptJsReady, setAcceptJsReady] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_address: '',
    delivery_state: '',
    delivery_city: '',
    delivery_zip: '',
    notes: '',
  });
  const [cardData, setCardData] = useState({
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cardCode: '',
  });
  const [billingData, setBillingData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  });
  const [sameAsDelivery, setSameAsDelivery] = useState(true);
  const [deliveryMethod, setDeliveryMethod] = useState('shipping'); // 'shipping' or 'pickup'

  // Calculate shipping, tax, and totals
  const subtotal = totalAmount;
  const isPickup = deliveryMethod === 'pickup';
  const shipping = isPickup ? { rate: 0, label: 'Pickup - No Shipping' } : getShippingRate(subtotal);
  const taxRate = isPickup ? 0.0575 : getTaxRate(formData.delivery_state);
  const taxAmount = subtotal * taxRate;
  const grandTotal = subtotal + shipping.rate + taxAmount;

  // Load Accept.js script
  useEffect(() => {
    const loadAcceptJs = async () => {
      try {
        const config = await axios.get(`${API}/payment/config`);
        setPaymentConfig(config.data);
        
        // Load Accept.js script
        const script = document.createElement('script');
        script.src = config.data.environment === 'sandbox' 
          ? 'https://jstest.authorize.net/v1/Accept.js'
          : 'https://js.authorize.net/v1/Accept.js';
        script.async = true;
        script.onload = () => setAcceptJsReady(true);
        script.onerror = () => console.error('Failed to load Accept.js script');
        document.head.appendChild(script);
      } catch (error) {
        console.error('Failed to load payment config:', error);
      }
    };
    loadAcceptJs();
  }, []);

  const handleCreateOrder = async (e) => {
    e.preventDefault();

    if (!isPickup && !formData.delivery_state) {
      toast.error('Please select your state');
      return;
    }

    setLoading(true);
    try {
      // Build full delivery address
      const fullAddress = isPickup 
        ? 'Pickup' 
        : `${formData.delivery_address}, ${formData.delivery_city}, ${formData.delivery_state} ${formData.delivery_zip}`;
      
      // Create order with shipping and tax info
      const orderData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        delivery_address: fullAddress,
        delivery_state: isPickup ? null : formData.delivery_state,
        delivery_city: isPickup ? null : formData.delivery_city,
        delivery_zip: isPickup ? null : formData.delivery_zip,
        delivery_date: null,
        delivery_method: deliveryMethod,
        notes: formData.notes,
        subtotal: subtotal,
        shipping_cost: shipping.rate,
        tax_amount: taxAmount,
        tax_rate: taxRate,
        items: items.map((item) => ({
          product_id: item.product.id,
          quantity: item.quantity,
        })),
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      setOrderId(orderResponse.data.id);
      
      // Pre-fill billing data from customer info if "same as delivery" is checked
      if (sameAsDelivery) {
        const nameParts = formData.customer_name.split(' ');
        setBillingData({
          ...billingData,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          address: formData.delivery_address,
          city: formData.delivery_city,
          state: formData.delivery_state,
          zip: formData.delivery_zip,
        });
      }
      
      setStep(3); // Go to payment step
      toast.success('Order created! Please enter payment details.');
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!paymentConfig || !acceptJsReady || !window.Accept) {
      toast.error('Payment system is still loading. Please wait a moment and try again.');
      return;
    }

    // Validate card data
    if (!cardData.cardNumber || !cardData.expMonth || !cardData.expYear || !cardData.cardCode) {
      toast.error('Please fill in all card details');
      return;
    }

    // Validate billing data
    if (!billingData.firstName || !billingData.lastName || !billingData.address || 
        !billingData.city || !billingData.state || !billingData.zip) {
      toast.error('Please fill in all billing address fields');
      return;
    }

    if (loading) return; // Prevent double-click
    setLoading(true);

    try {
      // Create Accept.js secure data
      const secureData = {
        authData: {
          clientKey: paymentConfig.publicClientKey,
          apiLoginID: paymentConfig.apiLoginId
        },
        cardData: {
          cardNumber: cardData.cardNumber.replace(/\s/g, ''),
          month: cardData.expMonth,
          year: cardData.expYear,
          cardCode: cardData.cardCode
        }
      };

      // Use Accept.js to tokenize card data
      window.Accept.dispatchData(secureData, async (response) => {
        if (response.messages.resultCode === 'Error') {
          const errorMessage = response.messages.message[0].text;
          toast.error(errorMessage);
          setLoading(false);
          return;
        }

        // Send payment token to backend with billing info
        try {
          const paymentResponse = await axios.post(`${API}/payment/process`, {
            order_id: orderId,
            data_descriptor: response.opaqueData.dataDescriptor,
            data_value: response.opaqueData.dataValue,
            amount: grandTotal,
            billing: billingData
          });

          if (paymentResponse.data.success) {
            toast.success('Payment successful!');
            clearCart();
            navigate(`/checkout/success?order_id=${orderId}`);
          } else {
            toast.error(paymentResponse.data.message || 'Payment was not successful. Please try again.');
            setLoading(false);
          }
        } catch (error) {
          console.error('Payment error:', error);
          const errorDetail = error.response?.data?.detail || 'Payment failed. Please try again.';
          toast.error(errorDetail);
          setLoading(false);
        }
      });
    } catch (error) {
      console.error('Payment setup error:', error);
      toast.error('Failed to process payment. Please try again.');
      setLoading(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  if (items.length === 0 && !orderId) {
    return (
      <div data-testid="empty-cart" className="min-h-screen pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-24 h-24 bg-sand rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="h-12 w-12 text-gray-400" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-forest mb-4">
            Your Cart is Empty
          </h1>
          <p className="text-gray-600 mb-8">
            Looks like you haven't added anything to your cart yet.
          </p>
          <Link to="/shop">
            <Button
              className="bg-forest text-white rounded-full px-8 py-6"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen pt-28 pb-16">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-forest mb-8 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          Continue Shopping
        </Link>

        <h1 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-8">
          {step === 1 ? 'Your Cart' : step === 2 ? 'Checkout' : 'Payment'}
        </h1>

        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-12">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-forest' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-forest text-white' : 'bg-gray-200'
            }`}>
              1
            </div>
            <span className="font-medium">Cart</span>
          </div>
          <div className={`flex-1 h-0.5 ${step >= 2 ? 'bg-forest' : 'bg-gray-200'}`} />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-forest' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 2 ? 'bg-forest text-white' : 'bg-gray-200'
            }`}>
              2
            </div>
            <span className="font-medium">Details</span>
          </div>
          <div className={`flex-1 h-0.5 ${step >= 3 ? 'bg-forest' : 'bg-gray-200'}`} />
          <div className={`flex items-center gap-2 ${step >= 3 ? 'text-forest' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 3 ? 'bg-forest text-white' : 'bg-gray-200'
            }`}>
              3
            </div>
            <span className="font-medium">Payment</span>
          </div>
        </div>

        {/* Step 1: Cart Items */}
        {step === 1 && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div
                  key={item.product.id}
                  className="bg-white p-6 rounded-2xl flex gap-6"
                  data-testid={`cart-item-${item.product.id}`}
                >
                  <img
                    src={item.product.images?.[0] || 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80'}
                    alt={item.product.name}
                    className="w-24 h-24 object-cover rounded-xl"
                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80'; }}
                  />
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-forest">
                      {item.product.name}
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">
                      ${item.product.price?.toFixed(2)} each
                    </p>
                    <div className="flex items-center gap-4 mt-4">
                      <div className="flex items-center border border-gray-200 rounded-full">
                        <button
                          onClick={() =>
                            updateQuantity(item.product.id, Math.max(1, item.quantity - 1))
                          }
                          className="p-2 hover:bg-gray-100 rounded-l-full"
                          data-testid={`decrease-${item.product.id}`}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-10 text-center">{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateQuantity(
                              item.product.id,
                              Math.min(item.product.stock, item.quantity + 1)
                            )
                          }
                          className="p-2 hover:bg-gray-100 rounded-r-full"
                          data-testid={`increase-${item.product.id}`}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.product.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                        data-testid={`remove-${item.product.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-forest text-lg">
                      ${(item.product.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="bg-white p-6 rounded-2xl h-fit sticky top-28">
              <h2 className="font-heading text-xl font-semibold text-forest mb-6">
                Order Summary
              </h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
                <div className="border-t pt-3 flex justify-between font-bold text-forest text-lg">
                  <span>Total</span>
                  <span>${totalAmount.toFixed(2)}</span>
                </div>
              </div>
              <Button
                onClick={() => setStep(2)}
                className="w-full bg-forest hover:bg-forest-light text-white py-6 rounded-full"
                data-testid="proceed-to-checkout-btn"
              >
                Proceed to Checkout
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Customer Details */}
        {step === 2 && (
          <form onSubmit={handleCreateOrder} className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white p-6 rounded-2xl">
                <h2 className="font-heading text-xl font-semibold text-forest mb-6">
                  Contact Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      value={formData.customer_name}
                      onChange={(e) =>
                        setFormData({ ...formData, customer_name: e.target.value })
                      }
                      required
                      className="rounded-xl"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) =>
                          setFormData({ ...formData, customer_email: e.target.value })
                        }
                        required
                        className="rounded-xl"
                        data-testid="customer-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone *
                      </label>
                      <Input
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) =>
                          setFormData({ ...formData, customer_phone: e.target.value })
                        }
                        required
                        className="rounded-xl"
                        data-testid="customer-phone-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl">
                <h2 className="font-heading text-xl font-semibold text-forest mb-6">
                  Delivery Method
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    data-testid="delivery-method-shipping"
                    onClick={() => setDeliveryMethod('shipping')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      deliveryMethod === 'shipping'
                        ? 'border-forest bg-forest/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Truck className={`h-6 w-6 mb-2 ${deliveryMethod === 'shipping' ? 'text-forest' : 'text-gray-400'}`} />
                    <p className="font-semibold text-forest">Ship to Address</p>
                    <p className="text-xs text-gray-500 mt-1">5-7 business days</p>
                  </button>
                  <button
                    type="button"
                    data-testid="delivery-method-pickup"
                    onClick={() => setDeliveryMethod('pickup')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      deliveryMethod === 'pickup'
                        ? 'border-forest bg-forest/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <MapPin className={`h-6 w-6 mb-2 ${deliveryMethod === 'pickup' ? 'text-forest' : 'text-gray-400'}`} />
                    <p className="font-semibold text-forest">Pickup</p>
                    <p className="text-xs text-gray-500 mt-1">No shipping cost</p>
                  </button>
                </div>
              </div>

              {deliveryMethod === 'shipping' && (
              <div className="bg-white p-6 rounded-2xl">
                <h2 className="font-heading text-xl font-semibold text-forest mb-6">
                  <Truck className="inline-block h-5 w-5 mr-2" />
                  Delivery Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address *
                    </label>
                    <Textarea
                      value={formData.delivery_address}
                      onChange={(e) =>
                        setFormData({ ...formData, delivery_address: e.target.value })
                      }
                      required
                      className="rounded-xl"
                      rows={2}
                      placeholder="123 Main Street, Apt 4B"
                      data-testid="delivery-address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <Input
                        type="text"
                        value={formData.delivery_city}
                        onChange={(e) =>
                          setFormData({ ...formData, delivery_city: e.target.value })
                        }
                        required
                        className="rounded-xl"
                        placeholder="Los Angeles"
                        data-testid="delivery-city-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code *
                      </label>
                      <Input
                        type="text"
                        value={formData.delivery_zip}
                        onChange={(e) =>
                          setFormData({ ...formData, delivery_zip: e.target.value })
                        }
                        required
                        className="rounded-xl"
                        placeholder="90210"
                        data-testid="delivery-zip-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <Select
                      value={formData.delivery_state}
                      onValueChange={(value) => setFormData({ ...formData, delivery_state: value })}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="delivery-state-select">
                        <SelectValue placeholder="Select your state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {US_STATES.map((state) => (
                          <SelectItem key={state.code} value={state.code}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      We currently ship only within the United States
                    </p>
                  </div>
                  <div className="p-4 bg-sand/50 rounded-xl border border-terracotta/20">
                    <div className="flex items-start gap-3">
                      <Package className="h-5 w-5 text-terracotta mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-forest">Shipping Information</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Orders are shipped within <strong>48 hours</strong> of being received. 
                          Delivery typically takes <strong>5 to 7 business days</strong> after shipping.
                        </p>
                        {subtotal >= FREE_SHIPPING_THRESHOLD ? (
                          <p className="text-sm text-green-600 font-medium mt-2">
                            ✓ You qualify for FREE shipping!
                          </p>
                        ) : (
                          <p className="text-sm text-terracotta mt-2">
                            Add ${(FREE_SHIPPING_THRESHOLD - subtotal).toFixed(2)} more for FREE shipping
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order Notes (Optional)
                    </label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      className="rounded-xl"
                      rows={2}
                      placeholder="Any special instructions for your order..."
                      data-testid="notes-input"
                    />
                  </div>
                </div>
              </div>
              )}

              {deliveryMethod === 'pickup' && (
              <div className="bg-white p-6 rounded-2xl">
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-forest">Pickup Selected</p>
                      <p className="text-sm text-gray-600 mt-1">
                        No shipping charges will be applied. Virginia state tax (5.75%) will be added. We'll send you an email when your order is ready for pickup.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order Notes (Optional)
                  </label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    className="rounded-xl"
                    rows={2}
                    placeholder="Any special instructions for your pickup order..."
                    data-testid="pickup-notes-input"
                  />
                </div>
              </div>
              )}
            </div>
            <div className="bg-white p-6 rounded-2xl h-fit sticky top-28">
              <h2 className="font-heading text-xl font-semibold text-forest mb-6">
                Order Summary
              </h2>
              <div className="space-y-3 mb-6">
                {items.map((item) => (
                  <div key={item.product.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.product.name} × {item.quantity}
                    </span>
                    <span>${(item.product.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className={shipping.rate === 0 ? 'text-green-600 font-medium' : ''}>
                      {isPickup ? 'Pickup' : shipping.rate === 0 ? 'FREE' : `$${shipping.rate.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Tax {isPickup ? '(VA - 5.75%)' : formData.delivery_state ? `(${formData.delivery_state} - ${(taxRate * 100).toFixed(2)}%)` : ''}
                    </span>
                    <span>{isPickup ? `$${taxAmount.toFixed(2)}` : formData.delivery_state ? `$${taxAmount.toFixed(2)}` : 'Select state'}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold text-forest text-lg">
                    <span>Total</span>
                    <span>${grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  type="submit"
                  disabled={loading || (!isPickup && !formData.delivery_state)}
                  className="w-full bg-terracotta hover:bg-terracotta/90 text-white py-6 rounded-full font-semibold text-lg"
                  data-testid="continue-to-payment-btn"
                >
                  {loading ? 'Processing...' : 'Continue to Payment'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="w-full rounded-full"
                >
                  Back to Cart
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <form onSubmit={handlePayment} className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Card Details */}
              <div className="bg-white p-6 rounded-2xl">
                <h2 className="font-heading text-xl font-semibold text-forest mb-6">
                  <CreditCard className="inline-block h-5 w-5 mr-2" />
                  Card Details
                </h2>
                
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 text-green-700">
                    <Lock className="h-4 w-4" />
                    <span className="text-sm font-medium">Secure payment powered by Authorize.net</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Number *
                    </label>
                    <Input
                      value={cardData.cardNumber}
                      onChange={(e) =>
                        setCardData({ ...cardData, cardNumber: formatCardNumber(e.target.value) })
                      }
                      placeholder="1234 5678 9012 3456"
                      maxLength={19}
                      required
                      className="rounded-xl"
                      data-testid="card-number-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Month *
                      </label>
                      <Input
                        value={cardData.expMonth}
                        onChange={(e) =>
                          setCardData({ ...cardData, expMonth: e.target.value.replace(/\D/g, '').slice(0, 2) })
                        }
                        placeholder="MM"
                        maxLength={2}
                        required
                        className="rounded-xl"
                        data-testid="exp-month-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year *
                      </label>
                      <Input
                        value={cardData.expYear}
                        onChange={(e) =>
                          setCardData({ ...cardData, expYear: e.target.value.replace(/\D/g, '').slice(0, 4) })
                        }
                        placeholder="YYYY"
                        maxLength={4}
                        required
                        className="rounded-xl"
                        data-testid="exp-year-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CVV *
                      </label>
                      <Input
                        type="password"
                        value={cardData.cardCode}
                        onChange={(e) =>
                          setCardData({ ...cardData, cardCode: e.target.value.replace(/\D/g, '').slice(0, 4) })
                        }
                        placeholder="123"
                        maxLength={4}
                        required
                        className="rounded-xl"
                        data-testid="cvv-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="bg-white p-6 rounded-2xl">
                <h2 className="font-heading text-xl font-semibold text-forest mb-6">
                  Billing Address
                </h2>
                
                <div className="space-y-4">
                  {/* Country Selector - First */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country *
                    </label>
                    <Select 
                      value={billingData.country} 
                      onValueChange={(value) => setBillingData({ ...billingData, country: value })}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="billing-country-select">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <Input
                        value={billingData.firstName}
                        onChange={(e) =>
                          setBillingData({ ...billingData, firstName: e.target.value })
                        }
                        required
                        className="rounded-xl"
                        data-testid="billing-firstname-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <Input
                        value={billingData.lastName}
                        onChange={(e) =>
                          setBillingData({ ...billingData, lastName: e.target.value })
                        }
                        required
                        className="rounded-xl"
                        data-testid="billing-lastname-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address *
                    </label>
                    <Input
                      value={billingData.address}
                      onChange={(e) =>
                        setBillingData({ ...billingData, address: e.target.value })
                      }
                      placeholder="123 Main Street"
                      required
                      className="rounded-xl"
                      data-testid="billing-address-input"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <Input
                        value={billingData.city}
                        onChange={(e) =>
                          setBillingData({ ...billingData, city: e.target.value })
                        }
                        required
                        className="rounded-xl"
                        data-testid="billing-city-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {billingData.country === 'US' ? 'State *' : 'State/Province *'}
                      </label>
                      <Input
                        value={billingData.state}
                        onChange={(e) =>
                          setBillingData({ ...billingData, state: e.target.value })
                        }
                        placeholder={billingData.country === 'US' ? 'FL' : 'Province'}
                        required
                        className="rounded-xl"
                        data-testid="billing-state-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {billingData.country === 'US' ? 'ZIP Code *' : 'Postal Code *'}
                      </label>
                      <Input
                        value={billingData.zip}
                        onChange={(e) =>
                          setBillingData({ ...billingData, zip: e.target.value })
                        }
                        placeholder={billingData.country === 'US' ? '12345' : 'Postal Code'}
                        required
                        className="rounded-xl"
                        data-testid="billing-zip-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-sand rounded-xl">
                  <p className="text-sm text-gray-600">
                    Your payment information is encrypted and secure. We never store your full card details.
                  </p>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white p-6 rounded-2xl h-fit sticky top-28">
              <h2 className="font-heading text-xl font-semibold text-forest mb-6">
                Order Summary
              </h2>
              <div className="space-y-3 mb-6">
                {items.map((item) => (
                  <div key={item.product.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.product.name} × {item.quantity}
                    </span>
                    <span>${(item.product.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className={shipping.rate === 0 ? 'text-green-600 font-medium' : ''}>
                      {shipping.rate === 0 ? 'FREE' : `$${shipping.rate.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Tax ({formData.delivery_state} - {(taxRate * 100).toFixed(2)}%)
                    </span>
                    <span>${taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold text-forest text-lg">
                    <span>Total</span>
                    <span>${grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-terracotta hover:bg-terracotta/90 text-white py-6 rounded-full font-semibold text-lg"
                  data-testid="place-order-btn"
                >
                  {loading ? 'Processing Payment...' : `Pay $${grandTotal.toFixed(2)}`}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="w-full rounded-full"
                  disabled={loading}
                >
                  Back to Details
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
